import Messenger, {
  boot,
  onHide,
  onShow,
  shutdown,
} from "@intercom/messenger-js-sdk";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import IntercomLoader from "web/Components/Common/Intercom/IntercomLoader";

import newRelicErrorReport from "web/utils/system/essentials/newRelicErrorReport";

import { Functionality } from "web/types/Employee";

import { useAppContext } from "web/context/app";
import { useGetCustomerDetailsQuery } from "web/features/customer/customerApiSlice";
import { useGetEmployeeDetailsQuery } from "web/features/employee/employeeApiSlice";
import { useGetIntercomTokenQuery } from "web/features/intercom/intercomApiSlice";

enum Regions {
  EU = "eu",
}

const Intercom = () => {
  const appId = process.env.INTERCOM_APP_ID ?? "";
  const { data: employeeDetails } = useGetEmployeeDetailsQuery();
  const { data: intercomToken, error } = useGetIntercomTokenQuery();
  const { data: customer } = useGetCustomerDetailsQuery();
  const location = useLocation();
  const { isMobile } = useAppContext();
  const initializedRef = useRef(false);

  const isIntercomAccessGranted = !!employeeDetails?.functionalities?.includes(
    Functionality.ENABLE_INTERCOM
  );
  const isIntercomDisabledForRoute = location.pathname.includes("checkout");

  const token = intercomToken?.token;
  const isReady = !!token;

  useEffect(() => {
    if (
      !isIntercomAccessGranted ||
      isIntercomDisabledForRoute ||
      !isReady ||
      !token
    )
      return;

    const settings = {
      app_id: appId,
      intercom_user_jwt: token,
      region: Regions.EU,
      name: `${customer?.firstname} ${customer?.lastname}`,
      language_override: customer?.language
        ? customer?.language.slice(0, 2)
        : "pl",
      user_language_mybenefit: customer?.language
        ? customer?.language.slice(0, 2)
        : "pl",
      background_color: "#102b3f",
      action_color: "#102b3f",
      has_mybenefit_id: true,
      ...(isMobile && {
        custom_launcher_selector: "#customIntercomLauncher",
        hide_default_launcher: true,
      }),
    };

    try {
      if (!initializedRef.current) {
        Messenger({ ...settings });
        onShow(() => {
          window.isIntercomPanelVisibleToUser = true;
        });
        onHide(() => {
          window.isIntercomPanelVisibleToUser = false;
        });
        initializedRef.current = true;
        return;
      }

      shutdown();
      boot(settings);
    } catch (err) {
      newRelicErrorReport(
        err,
        `Intercom: ${initializedRef.current ? "reboot" : "init"} failed`
      );
    }
  }, [
    isMobile,
    isIntercomAccessGranted,
    isIntercomDisabledForRoute,
    isReady,
    customer,
    token,
  ]);

  useEffect(() => {
    if (!error) return;

    newRelicErrorReport(error, "Intercom: getIntercomToken query failed");
  }, [error]);

  if (!isReady && isIntercomAccessGranted) {
    return <IntercomLoader />;
  }

  return null;
};

export default Intercom;
